import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { celebrateBadges } from "../lib/gamification";
import { FileSignature, Loader2 } from "lucide-react";

const TYPES = ["argumentative", "narrative", "expository", "compare-contrast", "analytical"];

export default function EssayStructure() {
  const [topic, setTopic] = useState("");
  const [type, setType] = useState("argumentative");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const onPlan = async () => {
    if (!topic.trim()) { toast.error("Enter a topic"); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/coach/essay-structure", { topic, essay_type: type });
      setResult(data);
      celebrateBadges(data._meta);
    } catch { toast.error("Could not plan"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-2xl bg-amber-100 text-amber-700 grid place-items-center"><FileSignature size={20}/></div>
          <h1 className="text-3xl font-bold" style={{fontFamily:'Outfit'}}>Essay Architect</h1>
        </div>
        <p className="text-slate-600">Plan your essay structure—we won't write it, we'll help you build it.</p>
      </div>

      <div className="tactile-card p-6 sm:p-8 space-y-4">
        <div>
          <label className="text-xs uppercase font-bold tracking-widest text-slate-500">Topic</label>
          <input data-testid="essay-topic" value={topic} onChange={(e)=>setTopic(e.target.value)}
            placeholder="e.g. Should kids have homework on weekends?"
            className="mt-1 w-full rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-amber-400 px-4 py-3 outline-none" />
        </div>
        <div>
          <label className="text-xs uppercase font-bold tracking-widest text-slate-500">Essay type</label>
          <div className="flex gap-2 mt-2 flex-wrap">
            {TYPES.map(t => (
              <button key={t} data-testid={`essay-type-${t}`} type="button"
                onClick={()=>setType(t)}
                className={`rounded-full px-4 py-2 text-sm font-bold border-2 ${type===t ? 'bg-amber-400 border-amber-500 text-slate-900':'bg-white border-slate-200 text-slate-700 hover:border-amber-300'}`}>
                {t}
              </button>
            ))}
          </div>
        </div>
        <button data-testid="essay-submit" onClick={onPlan} disabled={loading}
          className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold rounded-full px-6 py-3 tactile-shadow-amber inline-flex items-center gap-2 disabled:opacity-60">
          {loading ? <><Loader2 className="animate-spin" size={16}/> Planning...</> : "Build my outline"}
        </button>
      </div>

      {result && (
        <div data-testid="essay-result" className="space-y-4 fade-up">
          <Section title="Introduction">
            <p className="mb-3 text-slate-700">{result.intro?.goal}</p>
            <SubList label="Hook ideas" items={result.intro?.hook_ideas} />
            <SubList label="Thesis-shaping questions" items={result.intro?.thesis_questions} />
          </Section>
          <div className="space-y-4">
            {(result.body_paragraphs || []).map((bp, i) => (
              <Section key={i} title={`Body Paragraph ${i+1}`}>
                <p className="mb-3 text-slate-900 font-semibold">{bp.focus}</p>
                <SubList label="Evidence prompts" items={bp.evidence_prompts} />
                <SubList label="Transition starters" items={bp.transition_starters} />
              </Section>
            ))}
          </div>
          {result.counterargument && (
            <Section title="Counterargument">
              <p className="text-slate-700">{result.counterargument}</p>
            </Section>
          )}
          <Section title="Conclusion">
            <p className="mb-3 text-slate-700">{result.conclusion?.goal}</p>
            <SubList label="Strategies" items={result.conclusion?.strategies} />
          </Section>
          <Section title="Pre-submission checklist">
            <ul className="space-y-2">
              {(result.checklist || []).map((c,i) => (
                <li key={i} className="flex items-start gap-3"><span className="mt-1 w-5 h-5 rounded-md border-2 border-emerald-400" />{c}</li>
              ))}
            </ul>
          </Section>
        </div>
      )}
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="tactile-card p-6">
    <h3 className="text-xl font-bold mb-3" style={{fontFamily:'Outfit'}}>{title}</h3>
    {children}
  </div>
);
const SubList = ({ label, items }) => (
  <div className="mt-3">
    <div className="text-xs uppercase font-bold tracking-widest text-slate-500 mb-2">{label}</div>
    <ul className="list-disc pl-5 space-y-1 text-slate-800">{(items||[]).map((it,i)=>(<li key={i}>{it}</li>))}</ul>
  </div>
);
